import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import RatingStars from './RatingStars';
import { supabase } from '../supabase';
import { trackClick, trackReview } from '../utils/analytics';

const { FiX, FiPhone, FiGlobe, FiClock, FiMapPin, FiStar, FiMessageSquare, FiWifi, FiCoffee, FiTruck, FiCheck } = FiIcons;

const PlaceDetailsModal = ({ place, onClose, onRateUpdate }) => {
  const [userRating, setUserRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (place) fetchReviews();
  }, [place]);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('place_ratings_1740480000000')
        .select('*')
        .eq('place_id', place.id)
        .order('created_at', { ascending: false });
      if (!error) setReviews(data);
    } catch (err) {
      console.warn('Reviews fetch failed');
    }
  };

  const handleSubmitRating = async () => {
    if (userRating === 0 || submitting) return;
    setSubmitting(true);
    trackReview(place.name, userRating);
    
    try {
      const { error } = await supabase
        .from('place_ratings_1740480000000')
        .insert([{ place_id: place.id, rating: userRating, comment: comment }]);
      
      if (!error) {
        setUserRating(0);
        setComment('');
        fetchReviews();
        if (onRateUpdate) onRateUpdate();
      }
    } catch (err) {
      console.error('Rating submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExternalLink = (type, label) => {
    trackClick(`External Link: ${type}`, `PlaceDetails: ${label}`);
  };

  const getDetailGroups = () => {
    const tags = place.tags || {};
    const groups = [];
    const contact = {
      phone: tags.phone || tags['contact:phone'],
      website: tags.website || tags['contact:website'],
      facebook: tags['contact:facebook'] || tags.facebook
    };
    const hours = tags.opening_hours;
    const features = [];
    if (tags.wheelchair === 'yes') features.push({ label: 'Wheelchair Accessible', icon: FiCheck });
    if (tags.outdoor_seating === 'yes') features.push({ label: 'Outdoor Seating', icon: FiCoffee });
    if (tags.internet_access === 'wlan' || tags.wifi === 'yes') features.push({ label: 'Free Wi-Fi', icon: FiWifi });
    if (tags.delivery === 'yes') features.push({ label: 'Delivery Available', icon: FiTruck });
    return { contact, hours, features };
  };

  const { contact, hours, features } = getDetailGroups();

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center p-0 sm:p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" />
      <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="relative bg-white w-full max-w-lg rounded-t-[3rem] sm:rounded-[3rem] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" >
        <div className="h-48 bg-slate-100 relative flex-shrink-0">
          <img src={`https://static-maps.yandex.ru/1.x/?lang=en_US&ll=${place.lng},${place.lat}&z=17&l=map&size=600,300&pt=${place.lng},${place.lat},pm2rdl`} alt="Location Map" className="w-full h-full object-cover" />
          <button onClick={onClose} className="absolute top-6 right-6 p-3 bg-white/90 backdrop-blur-md rounded-full text-slate-900 shadow-xl hover:bg-white transition-all active:scale-90" >
            <SafeIcon icon={FiX} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <div className="mb-8">
            <span className="text-[10px] font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full uppercase tracking-widest mb-3 inline-block"> {place.tagType} </span>
            <h2 className="text-3xl font-black text-slate-950 tracking-tighter leading-none mb-3"> {place.name} </h2>
            <div className="flex items-center gap-4">
              <RatingStars rating={place.avgRating} count={place.reviewCount} />
              <div className="h-4 w-[1px] bg-slate-200"></div>
              <span className="text-xs font-bold text-slate-500 flex items-center gap-1"> {Math.round(place.distance)}m away </span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 mb-8">
            {contact.phone && (
              <a href={`tel:${contact.phone}`} onClick={() => handleExternalLink('Phone', place.name)} className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-indigo-50 transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-indigo-600 shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-all"> <SafeIcon icon={FiPhone} /> </div>
                <div className="flex flex-col"> <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Call Now</span> <span className="text-sm font-bold text-slate-900">{contact.phone}</span> </div>
              </a>
            )}
            {contact.website && (
              <a href={contact.website} target="_blank" rel="noopener noreferrer" onClick={() => handleExternalLink('Website', place.name)} className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-indigo-50 transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-indigo-600 shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-all"> <SafeIcon icon={FiGlobe} /> </div>
                <div className="flex flex-col"> <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Website</span> <span className="text-sm font-bold text-slate-900 truncate max-w-[200px]">{contact.website.replace(/^https?:\/\//, '')}</span> </div>
              </a>
            )}
          </div>
          <div className="bg-slate-950 rounded-[2rem] p-8 mb-10 text-white">
            <h4 className="text-lg font-black tracking-tight mb-2">How was your visit?</h4>
            <p className="text-slate-400 text-xs mb-6">Your feedback helps others find the best local spots.</p>
            <div className="flex justify-center mb-6">
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} onClick={() => { trackClick(`Rate ${s} Stars`, place.name); setUserRating(s); }} className="p-1 transition-transform active:scale-90" >
                    <SafeIcon icon={FiStar} className={`text-3xl ${s <= userRating ? 'fill-amber-400 text-amber-400' : 'text-slate-700'}`} />
                  </button>
                ))}
              </div>
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What did you like about this place? (Optional)" className="w-full bg-slate-900 border border-slate-800 rounded-2xl p-4 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors mb-4 resize-none h-24" />
            <button onClick={handleSubmitRating} disabled={userRating === 0 || submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-4 rounded-xl transition-all flex items-center justify-center gap-2" >
              {submitting ? <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div> : <><SafeIcon icon={FiMessageSquare} /> Submit Review</>}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PlaceDetailsModal;